import { useEffect, useSyncExternalStore } from "react";
import { ApiError } from "../../lib/api";
import { IN_APP, onFromApp } from "../../lib/inApp";
import { lessonsApi } from "../../lib/lessonsApi";
import { libraryApi } from "../../lib/libraryApi";
import { shelfItems, type ShelfItem } from "./shelf";

/**
 * The shelf and the count of lessons that wait, kept for the native screens. Why: on an iPad the list beside
 * the picked item is drawn again for each route, so it keeps what it last read and shows it at once, and a
 * change made in the item or in Lessons reaches the list.
 */

interface ShelfState {
  items: ShelfItem[] | null;
  waiting: number;
  err: string | null;
  at: number;
}

let state: ShelfState = { items: null, waiting: 0, err: null, at: 0 };
const listeners = new Set<() => void>();
let inFlight: Promise<void> | null = null;
let queued: Promise<void> | null = null;

const set = (next: Partial<ShelfState>) => {
  state = { ...state, ...next };
  for (const f of listeners) f();
};

/**
 * Read the shelf and the lessons that wait again. Why a read asked for during another waits its turn: the one
 * under way may have started before the change just made.
 */
export function reloadShelf(): Promise<void> {
  if (inFlight) {
    queued ??= inFlight.then(() => {
      queued = null;
      return reloadShelf();
    });
    return queued;
  }
  inFlight = Promise.all([libraryApi.list(), lessonsApi.list().catch(() => null)])
    .then(([lib, les]) => {
      set({
        items: shelfItems(lib.items),
        waiting: les ? les.lessons.filter((l) => l.state === "proposed").length : state.waiting,
        err: null,
        at: Date.now(),
      });
    })
    .catch((e: unknown) => set({ err: e instanceof ApiError ? e.message : String(e) }))
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

/** Load the shelf ahead of a tap on its tab, unless it is already here. */
export function warmShelf(): void {
  if (!state.at && !inFlight) void reloadShelf();
}

// Why: inside the phone app a screen shown again reads what it shows again (APP-MODE.md section 4).
if (IN_APP)
  onFromApp((m) => {
    if (m.kind === "shown" && listeners.size > 0) void reloadShelf();
  });

/** What one read stays good for while the owner moves between the list and an item. */
const FRESH_MS = 3000;

export function useShelf(): ShelfState {
  const s = useSyncExternalStore(
    (f) => {
      listeners.add(f);
      return () => listeners.delete(f);
    },
    () => state,
  );
  useEffect(() => {
    if (Date.now() - state.at > FRESH_MS) void reloadShelf();
  }, []);
  return s;
}
